/**
 * Coup de pouce — Attendu 1109 « réduire 3x + 2x, 3x × 2x, x + x, x × x »
 * (automatisme 4e, l. 704). DEUX pages STATIQUES, un cas par page,
 * partagées par les trois generator_keys : « 3x + 2x » (des paquets) et
 * « 3x × 2x » (un rectangle quadrillé). Chaque page nomme le piège de
 * l'autre : ajouter n'est pas multiplier (DEPP 2023 : 3x + 2x → 5x² est
 * l'erreur principale).
 *
 * L'image que l'élève a déjà : les paquets de 1096 (« 4 paquets de 3, c'est
 * 4 fois 3 »), le rectangle quadrillé de 1101 et 1059 (« m², c'est m × m »).
 * Page 1 : 3 paquets x bleus et 2 paquets x orange sur une ligne, « 3x + 2x
 * = 5x », « 3 paquets de x et 2 paquets de x : 5 paquets de x. », « Pas
 * 5x² : ajouter, ce n'est pas multiplier. » Page 2 : un rectangle de côtés
 * 3x et 2x coupé en 6 carrés de côté x, « 3x × 2x = 6x² », « 6 carrés de
 * côté x : 6 × x × x = 6x². », « Pas 6x : x × x, c'est x². »
 *
 * SACRIFIÉ : le couple (3 ; 2) dans les deux ordres, en somme et en produit
 * (gen_1109, COUPLE_SACRIFIE, auto-testé par gen1109.self.test.js). Largeur
 * bornée à 340 px, tailles de 1048.
 */

const ORANGE = '#EA580C';
const ORANGE_CLAIR = '#FED7AA';
const BLEU = '#2563EB';
const BLEU_CLAIR = '#DBEAFE';
const ENCRE = '#1E293B';

/** La lettre, dans la police italique de KaTeX (déclarée par katex.min.css) : le même x que dans les énoncés. */
const X = `<tspan font-family="KaTeX_Math, 'Times New Roman', serif" font-style="italic">x</tspan>`;

// ─── Page 1 : 3x + 2x, des paquets ────────────────────────────────────────

const PW = 52;
const PAS = 62;
const P0 = 200 - (5 * PAS - (PAS - PW)) / 2;
const PAQUETS = [0, 1, 2, 3, 4].map((k) => {
    const x = P0 + k * PAS;
    const bleu = k < 3;
    return `<rect x="${x}" y="52" width="${PW}" height="40" rx="8" fill="${bleu ? BLEU_CLAIR : ORANGE_CLAIR}" stroke="${bleu ? BLEU : ORANGE}" stroke-width="2"/>
  <text x="${x + PW / 2}" y="79" font-size="17" font-weight="bold" text-anchor="middle" fill="${bleu ? BLEU : ORANGE}">${X}</text>`;
}).join('\n  ');

const SVG_SOMME = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 226" width="100%" style="max-width:340px;height:auto;display:block" role="img"
     aria-label="3 x plus 2 x. Cinq paquets étiquetés x sur une ligne : 3 paquets bleus, puis 2 paquets orange. 3 x plus 2 x égale 5 x. 3 paquets de x et 2 paquets de x : 5 paquets de x. Pas 5 x au carré : ajouter, ce n'est pas multiplier.">
  <text x="200" y="30" font-size="16" font-weight="bold" text-anchor="middle" fill="${ENCRE}"><tspan fill="${BLEU}">3${X}</tspan> + <tspan fill="${ORANGE}">2${X}</tspan> = ?</text>
  ${PAQUETS}
  <text x="${P0 + 3 * PAS - 5}" y="79" font-size="16" font-weight="bold" text-anchor="middle" fill="${ENCRE}">+</text>

  <!-- le résultat -->
  <text x="200" y="146" font-size="20" font-weight="bold" text-anchor="middle" fill="${ENCRE}"><tspan fill="${BLEU}">3${X}</tspan> + <tspan fill="${ORANGE}">2${X}</tspan> = 5${X}</text>

  <!-- le geste, puis le piège -->
  <text x="200" y="184" font-size="12" text-anchor="middle" fill="${ENCRE}"><tspan font-weight="bold" fill="${BLEU}">3 paquets</tspan> de ${X} et <tspan font-weight="bold" fill="${ORANGE}">2 paquets</tspan> de ${X} : <tspan font-weight="bold">5 paquets</tspan> de ${X}.</text>
  <text x="200" y="208" font-size="12" text-anchor="middle" fill="${ENCRE}">Pas 5${X}² : ajouter, ce n'est pas multiplier.</text>
</svg>`.trim();

// ─── Page 2 : 3x × 2x, un rectangle quadrillé ─────────────────────────────

const C = 44;
const RX = 200 - (3 * C) / 2;
const RY = 50;
const CARRES = [0, 1, 2, 3, 4, 5].map((k) => {
    const x = RX + (k % 3) * C;
    const y = RY + Math.floor(k / 3) * C;
    return `<rect x="${x}" y="${y}" width="${C}" height="${C}" fill="${ORANGE_CLAIR}" stroke="${ORANGE}" stroke-width="1.6"/>
  <text x="${x + C / 2}" y="${y + C / 2 + 5}" font-size="14" font-weight="bold" text-anchor="middle" fill="${ORANGE}">${X}²</text>`;
}).join('\n  ');

const SVG_PRODUIT = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 264" width="100%" style="max-width:340px;height:auto;display:block" role="img"
     aria-label="3 x fois 2 x. Un rectangle de côtés 3 x en haut et 2 x à gauche, coupé en 6 carrés égaux étiquetés x au carré. 3 x fois 2 x égale 6 x au carré. 6 carrés de côté x : 6 fois x fois x égale 6 x au carré. Pas 6 x : x fois x, c'est x au carré.">
  <text x="200" y="30" font-size="16" font-weight="bold" text-anchor="middle" fill="${ENCRE}"><tspan fill="${BLEU}">3${X}</tspan> × <tspan fill="${BLEU}">2${X}</tspan> = ?</text>
  ${CARRES}
  <text x="${RX + (3 * C) / 2}" y="${RY - 8}" font-size="13" font-weight="bold" text-anchor="middle" fill="${BLEU}">3${X}</text>
  <text x="${RX - 8}" y="${RY + C + 5}" font-size="13" font-weight="bold" text-anchor="end" fill="${BLEU}">2${X}</text>

  <!-- le résultat -->
  <text x="200" y="182" font-size="20" font-weight="bold" text-anchor="middle" fill="${ENCRE}"><tspan fill="${BLEU}">3${X}</tspan> × <tspan fill="${BLEU}">2${X}</tspan> = <tspan fill="${ORANGE}">6${X}²</tspan></text>

  <!-- le geste, puis le piège -->
  <text x="200" y="220" font-size="12" text-anchor="middle" fill="${ENCRE}"><tspan font-weight="bold" fill="${ORANGE}">6 carrés</tspan> de côté ${X} : 6 × ${X} × ${X} = <tspan font-weight="bold" fill="${ORANGE}">6${X}²</tspan>.</text>
  <text x="200" y="244" font-size="12" text-anchor="middle" fill="${ENCRE}">Pas 6${X} : ${X} × ${X}, c'est ${X}².</text>
</svg>`.trim();

const AIDE = {
    pages: [
        { titre: 'Une somme : des paquets', svg: SVG_SOMME },
        { titre: 'Un produit : un rectangle', svg: SVG_PRODUIT },
    ],
};

const CP_1109 = {
    flash_texte_trou_saisie: AIDE,
    reduire_qcm_unique: AIDE,
    sens_tableau_vf: AIDE,
};

export default CP_1109;
